import { BookOpen } from "lucide-react";
import { motion } from "framer-motion";

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Left side - Auth Form */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 lg:max-w-[600px]"
      >
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex flex-col items-center justify-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2 
              }}
            >
              <BookOpen className="h-12 w-12 text-primary" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 text-3xl font-bold text-center text-foreground"
            >
              Numberz Insight
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-2 text-center text-sm text-muted-foreground"
            >
              Professional Accounting & Business Services
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <h2 className="text-center text-2xl font-semibold text-foreground">
              {title}
            </h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              {subtitle}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        >
          <div className="bg-card py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border">
            {children}
          </div>

          <div className="mt-6 text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              Trusted by 10,000+ businesses across Australia
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              🔒 Enterprise-grade security | ISO 27001 certified
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Right side - Illustration */}
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="hidden lg:flex flex-1 relative bg-primary/10 items-center justify-center p-12"
      >
        <div className="max-w-2xl text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              duration: 0.5,
              delay: 0.6,
              type: "spring",
              stiffness: 100
            }}
            className="relative w-full max-w-[400px] mx-auto"
          >
            <div className="absolute inset-0 bg-primary/5 rounded-full filter blur-3xl animate-pulse" />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-8 space-y-4"
          >
            <h2 className="text-3xl font-bold text-foreground">
              Welcome to Numberz Insight
            </h2>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Your trusted partner for professional accounting services, business registration, 
              and financial solutions across Australia.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}